import * as THREE from 'three';
import {
  MOVE_SPEED, TURN_SPEED, MOVE_ACCEL, MOVE_DECEL, TURN_ACCEL, TURN_DECEL,
  MOVE_ACCEL_AIR_MULT, TURN_ACCEL_AIR_MULT,
  JUMP_V, GRAVITY,
  RUN_PHASE_RATE, RUN_SWING,
} from '../config.js';
import { surfaceRadius } from '../world/planet.js';
import { isBlocked, ejectFromColliders } from '../world/colliders.js';
import { events } from '../core/events.js';
import { isBroken as isCommsBroken, canRepair as canRepairComms } from '../game/communicator.js';

// Player state lives in a single object so camera and other systems can read
// from it without round-tripping through the character mesh's transform.
export const player = {
  charPos:     new THREE.Vector3(0, 1, 0),   // position on unit sphere (north pole)
  charForward: new THREE.Vector3(1, 0, 0),   // tangent facing direction
  altitude: 0,
  vertV:    0,
  grounded: true,
  runPhase: 0,
  curMove:  0,    // current signed angular speed (rad/s)
  curTurn:  0,
  throwPhase: 0,  // seconds remaining in throw animation (0 = idle)
};

const THROW_DURATION = 0.55;

// Called by the interaction system the moment a crystal is thrown. Drives
// the right arm through a windup → release → settle arc in updatePlayer.
// Returns false if the player is already mid-throw so repeated F presses
// don't stack.
export function startThrow() {
  if (player.throwPhase > 0) return false;
  player.throwPhase = THROW_DURATION;
  return true;
}

export function isThrowing() { return player.throwPhase > 0; }

// When set, updatePlayer short-circuits — the cutscene is driving the
// character's transform directly and we don't want the controller's
// animations or physics to overwrite it.
let controllerSuspended = false;
export function suspendController(value) {
  controllerSuspended = value;
}

// Scratch vectors reused each frame — avoids per-frame allocations.
const _q       = new THREE.Quaternion();
const _m       = new THREE.Matrix4();
const _axis    = new THREE.Vector3();
const _right   = new THREE.Vector3();
const _tmpPos  = new THREE.Vector3();
const _tmpFwd  = new THREE.Vector3();
const _tmpWorld = new THREE.Vector3();
const _leanQ   = new THREE.Quaternion();
const _leanAxis = new THREE.Vector3(1, 0, 0);
const _bankQ    = new THREE.Quaternion();
const _bankAxis = new THREE.Vector3(0, 0, 1);

// Transient animation state (not part of `player`, since these are purely
// visual and would mess up serialisation).
let wasGrounded  = true;
let landingKick  = 0;    // 0 → 1 pulse when the character lands, decays back
let takeoffKick  = 0;    // 0 → 1 pulse at take-off, decays back faster

function readInput(keys) {
  const fwdIn =
    (keys.has('w') || keys.has('arrowup')   ? 1 : 0) -
    (keys.has('s') || keys.has('arrowdown') ? 1 : 0);
  const turnIn =
    (keys.has('a') || keys.has('arrowleft')  ? 1 : 0) -
    (keys.has('d') || keys.has('arrowright') ? 1 : 0);
  // Shift held → walk instead of run. Scales both forward and turning
  // so the character turns gently while creeping.
  const walk = keys.has('shift');
  return { fwdIn, turnIn, jump: keys.has(' '), walk };
}

const WALK_SPEED_FACTOR = 0.4;    // ~0.1 rad/s — below the cave wake threshold
const WALK_TURN_FACTOR  = 0.55;

// Exponential approach toward `target`, with separate accel/decel rates so
// releasing a key feels like planting the feet instead of sliding. Decel kicks
// in when slowing toward zero or reversing direction.
function easeSpeed(cur, target, accel, decel, dt, grounded, airMult) {
  const slowing = Math.abs(target) < Math.abs(cur) || target * cur < 0;
  const rate = grounded ? (slowing ? decel : accel) : accel * airMult;
  const k = 1 - Math.exp(-dt * rate);
  let next = cur + (target - cur) * k;
  if (Math.abs(next) < 1e-4) next = 0;
  return next;
}

export function updatePlayer(dt, keys, character) {
  if (controllerSuspended) return;
  const { fwdIn, turnIn, jump, walk } = readInput(keys);

  // ---------- Speed easing ----------
  const moveScale = walk ? WALK_SPEED_FACTOR : 1;
  const turnScale = walk ? WALK_TURN_FACTOR : 1;
  player.curMove = easeSpeed(
    player.curMove, fwdIn * MOVE_SPEED * moveScale,
    MOVE_ACCEL, MOVE_DECEL, dt, player.grounded, MOVE_ACCEL_AIR_MULT,
  );
  player.curTurn = easeSpeed(
    player.curTurn, turnIn * TURN_SPEED * turnScale,
    TURN_ACCEL, TURN_DECEL, dt, player.grounded, TURN_ACCEL_AIR_MULT,
  );

  // ---------- Turn (rotate facing around local up) ----------
  if (Math.abs(player.curTurn) > 1e-5) {
    _q.setFromAxisAngle(player.charPos, player.curTurn * dt);
    player.charForward.applyQuaternion(_q);
  }
  // Keep forward tangent to the sphere each frame.
  player.charForward
    .addScaledVector(player.charPos, -player.charForward.dot(player.charPos))
    .normalize();

  // ---------- Move (rotate position & forward around up × forward) ----------
  if (Math.abs(player.curMove) > 1e-5) {
    _axis.crossVectors(player.charPos, player.charForward).normalize();
    _q.setFromAxisAngle(_axis, player.curMove * dt);

    const newPos = _tmpPos.copy(player.charPos).applyQuaternion(_q).normalize();
    const newFwd = _tmpFwd.copy(player.charForward).applyQuaternion(_q);
    newFwd.addScaledVector(newPos, -newFwd.dot(newPos)).normalize();

    _tmpWorld.copy(newPos).multiplyScalar(surfaceRadius(newPos) + player.altitude);

    if (isBlocked(_tmpWorld)) {
      player.curMove = 0;     // plant feet against the obstacle
    } else {
      player.charPos.copy(newPos);
      player.charForward.copy(newFwd);
    }
  }

  // ---------- Jump / radial gravity ----------
  if (jump && player.grounded) {
    player.vertV = JUMP_V;
    player.grounded = false;
  }
  player.vertV -= GRAVITY * dt;
  const nextAltitude = player.altitude + player.vertV * dt;

  // Block upward motion when we'd rise into a collider (e.g. a cave
  // ceiling). Downward motion falls through colliders normally — the
  // eject pass further down handles landing-inside edge cases.
  if (player.vertV > 0) {
    _tmpWorld.copy(player.charPos)
      .multiplyScalar(surfaceRadius(player.charPos) + nextAltitude);
    if (isBlocked(_tmpWorld)) {
      player.vertV = 0;
    } else {
      player.altitude = nextAltitude;
    }
  } else {
    player.altitude = nextAltitude;
  }
  if (player.altitude <= 0) {
    player.altitude = 0;
    player.vertV = 0;
    player.grounded = true;
  }

  // ---------- Collision safety net ----------
  ejectFromColliders(player.charPos, player.altitude, player.charForward);

  // ---------- Animation ----------
  const moveFrac = Math.min(1, Math.abs(player.curMove) / MOVE_SPEED);
  if (player.grounded && moveFrac > 0.05) {
    player.runPhase += dt * RUN_PHASE_RATE * moveFrac;
  } else if (player.grounded) {
    // Settle toward 0 or π so legs end in a resting pose, not mid-stride.
    const target = Math.round(player.runPhase / Math.PI) * Math.PI;
    player.runPhase += (target - player.runPhase) * Math.min(1, dt * 10);
  }
  const swingAmt = player.grounded ? moveFrac * RUN_SWING : 0;
  const swing = Math.sin(player.runPhase) * swingAmt;
  const {
    legL, legR, armL, armR,
    upperBody, headGroup, hips, antennaTip,
    backLedR, backLedG, backLedA,
    hipY, headRestY,
  } = character.userData;
  const sinP = Math.sin(player.runPhase);
  const cosP = Math.cos(player.runPhase);

  // ---- Limb swing ----
  legL.rotation.x =  swing;
  legR.rotation.x = -swing;

  // Arms — contralateral swing + a forward posture bias so they hang slightly
  // ahead of the torso when moving. Elbow bend is faked by shortening the arm
  // on the forward half of its swing via scale.y (since we have no actual
  // elbow joint). Slight in/out z-sway makes them feel less pendulum-like.
  const armSwing    = swing * 0.95;
  const armFwdBias  = -0.1 * swingAmt;
  armL.rotation.x = -armSwing + armFwdBias;
  armR.rotation.x =  armSwing + armFwdBias;
  armL.rotation.z =  0.04 - Math.max(0,  swing) * 0.06 * swingAmt;
  armR.rotation.z = -0.04 + Math.max(0, -swing) * 0.06 * swingAmt;
  armL.scale.y = 1 - Math.max(0,  swing) * 0.22 * swingAmt;
  armR.scale.y = 1 - Math.max(0, -swing) * 0.22 * swingAmt;

  // ---- Foot lift per stride (fake knee bend) ----
  const liftAmt = 0.06 * swingAmt;
  legL.position.y = 0.45 + Math.max(0,  sinP) * liftAmt;
  legR.position.y = 0.45 + Math.max(0, -sinP) * liftAmt;

  // ---- Take-off and landing detection ----
  let justLanded = false;
  let justJumped = false;
  if (!wasGrounded && player.grounded) {
    landingKick = 1;
    justLanded = true;
  }
  if (wasGrounded && !player.grounded) {
    takeoffKick = 1;
    justJumped = true;
  }
  wasGrounded = player.grounded;
  landingKick = Math.max(0, landingKick - dt * 4);   // ~250 ms decay
  takeoffKick = Math.max(0, takeoffKick - dt * 7);   // ~140 ms — sharper snap

  // ---- Upper-body motion (twist + bob + sway + breathing + squash) ----
  // Bob peaks when a leg is at its extreme (foot planted / pushing off).
  const bob     = Math.abs(sinP) * 0.05 * swingAmt;
  // Twist: upper body counter-rotates with each step, matching the arm swing.
  const twist   = sinP * 0.09 * swingAmt;
  // Lateral sway: weight shifts side to side.
  const sway    = cosP * 0.035 * swingAmt;
  // Idle breathing when stationary.
  const breathe = (moveFrac < 0.05 && player.grounded)
    ? Math.sin(performance.now() / 1000 * 1.4) * 0.012
    : 0;
  // Landing squash + take-off stretch. Squash compresses the upper body on
  // impact; stretch elongates it upward at the moment of jump push-off.
  const squash  = Math.pow(landingKick, 1.4);
  const stretch = Math.pow(takeoffKick, 1.2);

  upperBody.position.y = hipY + bob + breathe;
  upperBody.rotation.y = twist;
  upperBody.rotation.z = sway;
  upperBody.scale.set(
    1 + squash * 0.08 - stretch * 0.05,
    1 - squash * 0.16 + stretch * 0.14,
    1 + squash * 0.08 - stretch * 0.05,
  );

  // Hip counter-rotation — pelvis twists opposite to the shoulders for a
  // natural contra-motion gait. Tracks 60% of the upper-body twist.
  hips.rotation.y = -twist * 0.6;

  // Head stabilisation — counter the upper body's twist, sway, and bob so the
  // head stays looking forward and doesn't bounce as much as the torso.
  // Humans use vestibulo-ocular reflex for exactly this effect.
  let headY = -twist * 0.7;
  // Idle fidget: a slow left/right look when basically standing still.
  if (moveFrac < 0.05 && player.grounded) {
    headY += Math.sin(performance.now() / 1000 * 0.22) * 0.09;
  }
  headGroup.rotation.y = headY;
  headGroup.rotation.z = -sway  * 0.5;
  headGroup.position.y = headRestY - bob * 0.4;

  // Keep the head more level when the body is leaning forward to run. The
  // character tips forward by ~0.12 rad at full speed; countering it by 55%
  // on the head's local X keeps the horizon roughly steady in the camera.
  headGroup.rotation.x = moveFrac * 0.12 * 0.55;

  if (!player.grounded) {
    // Airborne pose — kept deliberately subtle. Arms essentially hang
    // naturally with the same gentle outward bias as the walk rest pose,
    // legs with a small forward tuck. The character reads as "briefly in
    // the air" rather than striking a pose.
    const vFrac = Math.max(-1, Math.min(1, player.vertV / JUMP_V));

    // Peak reach — sharp bump near the top of the jump (vertV near zero). The
    // cubic falloff keeps the pose subtle except in the brief apex window.
    const peakness = Math.pow(Math.max(0, 1 - Math.abs(vFrac)), 3);

    // Preserve the "mid-stride" character of a running jump. `runPhase`
    // froze at take-off, so combined with the current horizontal speed we
    // can bake that stride into the air pose. Blends smoothly to a clean
    // straight-leg pose when the character was barely moving.
    const runMomentum = Math.min(1, Math.abs(player.curMove) / MOVE_SPEED);
    const stride = Math.sin(player.runPhase) * runMomentum;
    const legLeanL =  stride * 0.3;   // +back / -forward
    const legLeanR = -stride * 0.3;

    legL.rotation.x = legLeanL - 0.04 * (1 - peakness);
    legR.rotation.x = legLeanR - 0.04 * (1 - peakness);
    // Forward (swinging) leg is the bent one — stance leg stays straighter.
    legL.scale.y = 0.97 - Math.max(0, -legLeanL) * 0.08 + peakness * 0.03;
    legR.scale.y = 0.97 - Math.max(0, -legLeanR) * 0.08 + peakness * 0.03;
    legL.position.y = 0.45;
    legR.position.y = 0.45;

    // Arms: contralateral swing matching the frozen stride + the apex reach.
    // Forward-reaching arm pairs with the back leg on the other side.
    const armSwing = stride * 0.26;
    const armFlare = 0.06 + Math.max(0, -vFrac) * 0.05 + peakness * 0.08;
    const armReach = peakness * 0.28;
    armL.rotation.x = -armSwing - 0.02 - armReach;
    armR.rotation.x =  armSwing - 0.02 - armReach;
    armL.rotation.z =  armFlare;
    armR.rotation.z = -armFlare;
    armL.scale.y = 0.95;
    armR.scale.y = 0.95;

    // Neutral upper body, hips, and head.
    upperBody.rotation.y = 0;
    upperBody.rotation.z = 0;
    hips.rotation.y = 0;
    headGroup.rotation.x = 0;
    headGroup.rotation.y = 0;
    headGroup.rotation.z = 0;
    headGroup.position.y = headRestY;
  } else {
    // Make sure airborne scaling doesn't bleed into grounded frames.
    legL.scale.y = 1;
    legR.scale.y = 1;
  }

  // ---- Throw animation (overrides the right arm for ~0.55 s) ----
  // Windup: arm rotates back and up. Release: fast forward whip past
  // perpendicular. Settle: ease back to the run pose.
  if (player.throwPhase > 0) {
    const progress = 1 - player.throwPhase / THROW_DURATION;
    let throwX, throwZ, armStretch;
    if (progress < 0.44) {
      // Windup — ease-out back swing.
      const u = progress / 0.44;
      const eased = 1 - Math.pow(1 - u, 3);
      throwX = -1.45 * eased;
      throwZ = -0.04 - 0.25 * eased;            // arm cocks outward
      armStretch = 1.02;
    } else if (progress < 0.64) {
      // Release — fast whip from windup to follow-through.
      const u = (progress - 0.44) / 0.2;
      const eased = u * u;                      // ease-in for acceleration
      throwX = -1.45 + (1.15 - -1.45) * eased;
      throwZ = -0.29 + 0.2 * eased;
      armStretch = 1.1 - eased * 0.1;            // brief stretch during release
    } else {
      // Settle — glide back to neutral.
      const u = (progress - 0.64) / 0.36;
      const eased = 1 - Math.pow(1 - u, 2);
      throwX = 1.15 * (1 - eased);
      throwZ = -0.09 + 0.05 * eased;
      armStretch = 1.0;
    }
    armR.rotation.x = throwX;
    armR.rotation.z = throwZ;
    armR.scale.y = armStretch;

    // Slight torso counter-twist so the throw reads from the back.
    upperBody.rotation.y = (upperBody.rotation.y || 0)
      + Math.sin(progress * Math.PI) * -0.18;

    player.throwPhase = Math.max(0, player.throwPhase - dt);
  }

  // Antenna beacon: gentle breathing glow plus a narrow bright flash every
  // few seconds. Pure visual — doesn't depend on player state.
  const t = performance.now() / 1000;
  const breath = 0.4 + 0.25 * Math.sin(t * 1.8);
  const flash  = Math.pow(Math.max(0, Math.sin(t * 1.3)), 18);
  antennaTip.material.emissiveIntensity = breath + flash * 0.9;

  // Suit life — three console LEDs on the top of the pack. High baseline so
  // all three are always clearly on; the per-LED animation rides on top.
  // Each cycles at its own rhythm so they never pulse in sync.
  const heartbeat = Math.pow(Math.max(0, Math.sin(t * 1.4)), 3);
  backLedR.material.emissiveIntensity = 1.0 + heartbeat * 1.3;

  // Green: gentle slow pulse + occasional brief blink.
  const greenSlow  = 0.5 + 0.5 * Math.sin(t * 0.8);
  const greenBlink = Math.pow(Math.max(0, Math.sin(t * 0.32)), 24);
  backLedG.material.emissiveIntensity = 1.0 + greenSlow * 0.3 + greenBlink * 1.2;

  // Amber LED = communicator status indicator. Reads at a glance:
  //   • broken (no comms): dim erratic flicker — "signal lost".
  //   • ready to repair:  bright attention-getting pulse — "user action".
  //   • online:           calm steady glow — "channel open".
  if (!isCommsBroken()) {
    backLedA.material.emissiveIntensity = 1.3 + Math.sin(t * 0.55) * 0.15;
  } else if (canRepairComms()) {
    const readyPulse = 0.5 + 0.5 * Math.sin(t * 1.6);
    backLedA.material.emissiveIntensity = 1.2 + readyPulse * 1.3;
  } else {
    // Stuttered low flicker — second sine inside the first gives the irregular
    // cadence of a failing signal rather than a clean pulse.
    const flickerPhase = t * 6 + Math.sin(t * 2.3) * 2.5;
    const flicker = 0.5 + 0.5 * Math.sin(flickerPhase);
    backLedA.material.emissiveIntensity = 0.25 + flicker * flicker * 0.8;
  }

  // ---------- Place character in world ----------
  _right.crossVectors(player.charPos, player.charForward).normalize();
  _m.makeBasis(_right, player.charPos, player.charForward); // x=right, y=up, z=forward
  character.quaternion.setFromRotationMatrix(_m);

  // Forward lean while running on the ground — rotates the whole body a few
  // degrees around local X (right) so the character pitches into their stride.
  // Applied after the basis rotation so it composes as a local lean.
  if (player.grounded && moveFrac > 0.01) {
    const leanAmount = moveFrac * 0.12;
    _leanQ.setFromAxisAngle(_leanAxis, leanAmount);
    character.quaternion.multiply(_leanQ);
  }

  // Turn banking — tilt the whole body around the local forward axis (Z) when
  // turning while running, like carving a ski turn. Scaled by both turn rate
  // and forward speed so a stationary spin doesn't wobble but a running
  // swerve carves hard. `player.curTurn` is already eased, so the bank
  // smoothly ramps in and out.
  if (player.grounded) {
    const bankAmount = (player.curTurn / TURN_SPEED) * moveFrac * 0.11;
    if (Math.abs(bankAmount) > 1e-4) {
      _bankQ.setFromAxisAngle(_bankAxis, bankAmount);
      character.quaternion.multiply(_bankQ);
    }
  }

  const groundR = surfaceRadius(player.charPos);
  character.position.copy(player.charPos).multiplyScalar(groundR + player.altitude);

  // Emit take-off / landing events once the final world position is in place,
  // so listeners (dust puffs, sound, etc.) spawn at the correct spot.
  if (justJumped) {
    events.emit('character:jumped', {
      position: character.position.clone(),
      up:       player.charPos.clone(),
    });
  }
  if (justLanded) {
    events.emit('character:landed', {
      position: character.position.clone(),
      up:       player.charPos.clone(),
    });
  }
}
